import { createBrowserRouter, type RouteObject } from "react-router";
import { Spinner } from "@/components";
import { LoginPage } from "./auth/LoginPage";
import { RequireSession } from "./auth/RequireSession";
import { featureRoutes } from "./featureRoutes";
import { AppLayout } from "./layout/AppLayout";
import { NotFoundPage, RouteErrorPage } from "./RouteErrorPage";
import styles from "./pages.module.css";

/** Shown while a lazy route (code-split page) loads on the first visit. */
function InitialLoading() {
  return (
    <div className={styles.fullPage}>
      <Spinner size="md" label="Loading…" />
    </div>
  );
}

/** The living style guide is registered only in dev builds (tree-shaken from production). */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: "/dev/components",
        lazy: async () => ({ Component: (await import("./dev/ComponentsPage")).ComponentsPage }),
        handle: { title: "Components" },
      },
    ]
  : [];

export function buildRoutes(): RouteObject[] {
  return [
    {
      HydrateFallback: InitialLoading,
      children: [
        { path: "/login", element: <LoginPage />, handle: { title: "Sign in" } },
        {
          path: "/phone-camera",
          lazy: async () => ({
            Component: (await import("@/features/inventory/pages/PhoneCapturePage")).PhoneCapturePage,
          }),
          handle: { title: "Phone camera" },
        },
        ...devRoutes,
        {
          element: (
            <RequireSession>
              <AppLayout />
            </RequireSession>
          ),
          children: [
            {
              // Errors render inside the layout, so the sidebar and header stay usable.
              errorElement: <RouteErrorPage />,
              children: [
                ...(featureRoutes as RouteObject[]),
                { path: "*", element: <NotFoundPage />, handle: { title: "Not found" } },
              ],
            },
          ],
        },
      ],
    },
  ];
}

export function createAppRouter() {
  return createBrowserRouter(buildRoutes());
}
